/**
 * Single source of truth for turning a backend plan/package into a tracking
 * item. Every event goes through here, so a new plan shape (promo SKUs, a new
 * provider, a seasonal bundle) can only break in one place instead of silently
 * mis-tagging one funnel — see §6.4 of the tracking spec.
 */

import { HomeRouterPlan } from "../../../types";
import { ItemCategory, TrackingItem, money } from "./dataLayer";

/** `discountRate` comes back as a 0..1 fraction, not a percentage. */
export const discountedPrice = (plan: any): number => {
  const price = Number(plan?.price) || 0;
  const rate = Number(plan?.discountRate) || 0;
  return money(price - price * rate);
};

/** "1" = physical SIM, "2" = eSIM; contractTypeId 1 = prepaid. */
const planVariant = (plan: any): string | undefined => {
  const sim =
    plan?.simTypeId === "2" || plan?.simTypeId === 2
      ? "esim"
      : plan?.simTypeId === "1" || plan?.simTypeId === 1
      ? "sim"
      : undefined;
  const contract =
    plan?.contractTypeId === 1 || plan?.contractTypeId === "1"
      ? "prepaid"
      : plan?.contractTypeId
      ? "postpaid"
      : undefined;
  const parts = [contract, sim].filter(Boolean);
  return parts.length ? parts.join("_") : undefined;
};

/**
 * Mobile plan or travel eSIM, from the raw API shape returned by
 * getPlanbyId / getFilterPlans / getPlans — not the display-mapped shape the
 * list components build for rendering, which drops providerId and discountRate.
 */
export const planToItem = (
  plan: any,
  opts: {
    category?: ItemCategory;
    /** Provider object or plain name, when the caller already resolved it. */
    provider?: any;
    index?: number;
  } = {}
): TrackingItem => {
  const price = discountedPrice(plan);
  const listPrice = money(plan?.price);
  const brand =
    typeof opts.provider === "string" ? opts.provider : opts.provider?.name;

  const item: TrackingItem = {
    item_id: String(plan?.id ?? ""),
    item_name: plan?.name || "",
    item_category: opts.category || "mobile_plan",
    price,
  };
  if (brand) item.item_brand = brand;
  const variant = planVariant(plan);
  if (variant) item.item_variant = variant;
  if (listPrice > price) item.discount = money(listPrice - price);
  if (typeof opts.index === "number") item.index = opts.index;
  return item;
};

export const planListToItems = (
  plans: any[],
  opts: { category?: ItemCategory; providers?: Record<number, any> } = {}
): TrackingItem[] =>
  // capped at 20 — GA4 drops oversized payloads and the platforms only use the
  // first handful anyway
  (plans || []).slice(0, 20).map((plan, index) =>
    planToItem(plan, {
      category: opts.category,
      provider: plan?.provider || opts.providers?.[plan?.providerId],
      index,
    })
  );

/**
 * Home routers are served from a static config and identified by their index in
 * that array (that is the `:id` the routes use), so there is no backend id to
 * key on. `show === false` means the discount is not active.
 */
export const routerToItem = (
  plan: HomeRouterPlan,
  index: number,
  lang: string,
  provider?: any
): TrackingItem => {
  const price = money(
    plan.show === false ? plan.regularPrice : plan.discountedPrice
  );
  const item: TrackingItem = {
    item_id: "router_" + index,
    item_name: lang === "ar" ? plan.nameAr : plan.nameEn,
    item_category: "home_router",
    price,
    index,
  };
  const brand = typeof provider === "string" ? provider : provider?.name;
  if (brand) item.item_brand = brand;
  if (plan.commitmentPeriodMonths)
    item.item_variant = plan.commitmentPeriodMonths + "m_commitment";
  if (plan.regularPrice > price) item.discount = money(plan.regularPrice - price);
  return item;
};

/**
 * What the customer is actually charged: the fee row of type 4 is the grand
 * total (plan price + one-time service fee) and is the amount posted to the
 * gateway. The plan price alone understates revenue on every platform, so this
 * is what `ecommerce.value` must carry — `items[].price` stays the plan price.
 */
export const chargedTotal = (fees: any[] | undefined, fallback: number): number => {
  const total = (fees || []).find((f: any) => f?.type === 4)?.value;
  return total !== undefined && total !== null ? money(total) : money(fallback);
};
